import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Play, Pause, Volume2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const AUDIO_URL = 'https://cdn.magicpatterns.com/uploads/1sZwn97yChXapPrZ6fe6J7/WhatsApp_Video_2026-02-08_at_3.49.53_AM.undefined';
const BAR_COUNT = 45;
export function AudioPreview() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const mediaRef = useRef<HTMLVideoElement>(null);
  const barHeights = useRef(Array.from({
    length: BAR_COUNT
  }, () => Math.random() * 0.7 + 0.3)).current;
  useEffect(() => {
    const media = mediaRef.current;
    if (!media) return;
    const updateTime = () => {
      setCurrentTime(media.currentTime);
      setProgress(media.currentTime / (media.duration || 1));
    };
    const handleLoadedMetadata = () => {
      setDuration(media.duration);
    };
    const handleEnded = () => {
      setIsPlaying(false);
      setProgress(0);
      setCurrentTime(0);
      media.currentTime = 0;
    };
    media.addEventListener('timeupdate', updateTime);
    media.addEventListener('loadedmetadata', handleLoadedMetadata);
    media.addEventListener('ended', handleEnded);
    return () => {
      media.removeEventListener('timeupdate', updateTime);
      media.removeEventListener('loadedmetadata', handleLoadedMetadata);
      media.removeEventListener('ended', handleEnded);
    };
  }, []);
  const togglePlay = useCallback(() => {
    if (mediaRef.current) {
      if (isPlaying) {
        mediaRef.current.pause();
      } else {
        mediaRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  }, [isPlaying]);
  const formatTime = (s: number) => {
    if (isNaN(s)) return '0:00';
    const mins = Math.floor(s / 60);
    const secs = Math.floor(s % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  return <motion.div initial={{
    opacity: 0,
    y: 30
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.6
  }} className="w-full" data-id="element-4">
      {/* Hidden video element for audio playback */}
      <video ref={mediaRef} src={AUDIO_URL} className="hidden" playsInline data-id="element-5" />

      {/* Header */}
      <div className="text-center mb-6" data-id="element-6">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#25D366]/10 border border-[#25D366]/20 rounded-full text-[#25D366] text-xs font-bold tracking-wider uppercase mb-4" data-id="element-7">
          <Volume2 className="w-3.5 h-3.5" data-id="element-8" />
          Audio Preview
        </div>
        <h3 className="text-2xl md:text-3xl font-bold text-white" data-id="element-9">
          Escucha antes de comprar
        </h3>
      </div>

      {/* Player Card */}
      <div className="bg-[#111b21] border border-white/10 rounded-2xl p-5 md:p-6 relative overflow-hidden" data-id="element-10">
        {/* Ambient glow when playing */}
        <AnimatePresence data-id="element-11">
          {isPlaying && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="absolute inset-0 bg-[#25D366]/5 pointer-events-none" data-id="element-12" />}
        </AnimatePresence>

        {/* Player row: Play + Waveform + Time */}
        <div className="flex items-center gap-4" data-id="element-13">
          {/* Play/Pause Button */}
          <motion.button onClick={togglePlay} whileTap={{
          scale: 0.9
        }} className="relative min-w-[52px] w-[52px] h-[52px] rounded-full bg-[#25D366] flex items-center justify-center text-black shadow-[0_0_20px_rgba(37,211,102,0.3)] hover:shadow-[0_0_30px_rgba(37,211,102,0.5)] transition-shadow" data-id="element-14">
            {isPlaying ? <Pause className="w-5 h-5 fill-current" data-id="element-15" /> : <Play className="w-5 h-5 fill-current ml-0.5" data-id="element-16" />}

            {isPlaying && <motion.div animate={{
            scale: [1, 1.5],
            opacity: [0.3, 0]
          }} transition={{
            duration: 1.5,
            repeat: Infinity
          }} className="absolute inset-0 rounded-full border-2 border-[#25D366]" data-id="element-17" />}
          </motion.button>

          {/* Waveform + progress */}
          <div className="flex-1 flex flex-col gap-2" data-id="element-18">
            <div className="flex items-end gap-[2px] h-10" data-id="element-19">
              {barHeights.map((height, i) => {
              const barProgress = i / BAR_COUNT;
              const isPast = barProgress < progress;
              const isNear = isPlaying && Math.abs(barProgress - progress) < 0.06;
              return <motion.div key={i} className="flex-1 rounded-full min-w-[2px] origin-bottom" style={{
                height: `${height * 100}%`,
                backgroundColor: isPast ? '#25D366' : '#2a3942'
              }} animate={isNear ? {
                scaleY: [1, 1.4, 0.7, 1.2, 1],
                transition: {
                  duration: 0.4,
                  repeat: Infinity,
                  repeatType: 'reverse' as const
                }
              } : {
                scaleY: 1
              }} data-id="element-20" />;
            })}
            </div>

            {/* Progress bar */}
            <div className="w-full h-[3px] bg-[#2a3942] rounded-full overflow-hidden" data-id="element-21">
              <div className="h-full bg-[#25D366] rounded-full transition-all duration-100" style={{
              width: `${progress * 100}%`
            }} data-id="element-22" />
            </div>
          </div>

          {/* Time */}
          <div className="text-xs font-mono text-gray-500 min-w-[36px] text-right" data-id="element-23">
            <span className={isPlaying ? 'text-[#25D366]' : ''} data-id="element-24">
              {formatTime(currentTime)}
            </span>
          </div>
        </div>
      </div>

      {/* Subtext */}
      <p className="text-center text-xs text-gray-600 mt-3 mb-6" data-id="element-25">
        🎧 Escucha el mensaje real • Sube el volumen
      </p>
    </motion.div>;
}